"use client";

import React from "react";

import Image from "next/image";
import Chip from "@/components/global/ui/Chip";
import DecorationLine from "@/components/global/ui/DecorationLine";
import {cn} from "@/lib/utils";
import {PortableText} from "@portabletext/react";
import {richTextComponent} from "@/sanity/sanityPortableText";
import BackgroundVideo from "@/components/global/utils/BackgroundVideo";

import {PageKomplettausbildungSchema} from "@/sanity/pages/PageKomplettausbildungTemplate";

import bento1 from "@/public/komplettausbildung/bento1.png";
import bento1EN from "@/public/komplettausbildung/bento1_en.png";
import bento3 from "@/public/komplettausbildung/bento3.png";
import bento4 from "@/public/komplettausbildung/bento4.jpg";
import bento5 from "@/public/komplettausbildung/bento5.png";
import bento5EN from "@/public/komplettausbildung/bento5_en.jpg";
import bento6Fallback from "@/public/home-new/card2-fallback.png";
import fireFallbackDesktop from "@/public/home-new/fire-fallback-desktop.png";

export default function BentoSection({
  data,
  className
}: {
  data: PageKomplettausbildungSchema;
  className?: string;
}) {
  return (
    <section className={cn(
      "relative overflow-clip pt-[13rem] lg:pt-[18rem]",
      className
    )}>
      <DecorationLine
        className="top-0"
        orientation="vertical"
        position="left"
      />
      <DecorationLine
        className="top-0"
        orientation="vertical"
        position="right"
      />
      <DecorationLine
        className="top-0 left-0"
        orientation="horizontal"
      />

      <div className="container flex flex-col items-center">
        <div className="w-[min(187.75rem,100%)] flex flex-col items-center mb-[4rem] lg:mb-[5rem]">
          <Chip
            text={data?.bentoChip?.text}
            image={data?.bentoChip?.icon}
          />

          {data?.bentoTitle && (
            <div className="richtext">
              <PortableText
                value={data?.bentoTitle}
                components={richTextComponent}
              />
            </div>
          )}
        </div>

        <div className="w-[min(187.75rem,100%)] flex flex-col lg:flex-row items-center gap-[2rem] lg:gap-[1rem]">
          {/* bento column 1 */}
          <div className="w-[min(59rem,100%)] flex-shrink-0 flex flex-col gap-[2rem] lg:gap-[1rem]">
            {/* bento item 1 */}
            <div className="h-[40rem] lg:h-auto lg:aspect-[472/400] w-full bg-[#EDECEB] flex-shrink-0 relative flex items-start justify-start p-[2.5rem] sm:p-[3rem] lg:p-[4rem] overflow-clip">
              {data?.bentoItem1Text && (
                <div className="richtext richtext-start prose-strong:font-normal prose-p:text-[2.25rem] lg:prose-p:text-[2.5rem] w-[min(38.875rem,100%)] relative">
                  <PortableText
                    value={data?.bentoItem1Text}
                    components={richTextComponent}
                  />
                </div>
              )}

              <Image
                className="absolute left-1/2 -translate-x-1/2 -bottom-[2rem] !w-[calc(100%-7rem)] pointer-events-none"
                src={data?.settings?.language === "en" ? bento1EN : bento1 }
                quality={95}
                alt="phone with app"
              />
            </div>

            {/* bento item 2 */}
            <div className="h-[41.25rem] lg:h-auto lg:aspect-[472/400] w-full bg-[#0A1A1D] flex-shrink-0 relative flex items-center justify-center p-[2.5rem] lg:py-[4rem]">
              <div className="absolute inset-0 h-full w-full mix-blend-screen pointer-events-none">
                <BackgroundVideo
                  className="[&_video]:object-fill"
                  playbackId={"4YcQver7zBeOPhRwRJnkpKrAj8eRC1yqdj1NBPbCOZc"}
                  fallbackImage={fireFallbackDesktop}
                />
              </div>

              {data?.bentoItem2Text && (
                <div className="richtext prose-p:text-[3.75rem] lg:prose-p:text-[5rem] prose-p:leading-none prose-p:text-white/60 prose-p:!font-light prose-p:font-ivy prose-p:italic prose-strong:!font-normal prose-strong:text-white">
                  <PortableText
                    value={data?.bentoItem2Text}
                    components={richTextComponent}
                  />
                </div>
              )}
            </div>
          </div>

          {/* bento column 2 */}
          {/* bento item 3 */}
          <div className="w-[min(59rem,100%)] lg:w-auto lg:grow flex flex-col justify-end gap-[2rem] lg:gap-[1rem] relative bg-[#D9F9B9]">
            <BackgroundVideo
              playbackId={"WWKzOI96tyodqjdvLLlutfhD100QwAhyFb2JwBjGb4500"}
              fallbackImage={bento6Fallback}
            />

            <Image
              className="!w-full object-contain relative"
              src={bento3}
              quality={95}
              alt="Image of basti talking"
            />

            <div className="absolute z-10 left-[2.5rem] right-[2.5rem] bottom-[2.5rem] lg:left-[4rem] lg:right-auto lg:bottom-[4rem]">
              {data?.bentoItem3Text && (
                <div className="richtext richtext-start prose-p:text-white/60 prose-strong:text-white prose-strong:font-normal prose-p:text-[2.25rem] lg:prose-p:text-[2.5rem] w-[min(40rem,100%)]">
                  <PortableText
                    value={data?.bentoItem3Text}
                    components={richTextComponent}
                  />
                </div>
              )}
            </div>
          </div>

          {/* bento column 3 */}
          <div className="w-[min(59rem,100%)] flex-shrink-0 flex flex-col gap-[2rem] lg:gap-[1rem]">
            {/* bento item 4 */}
            <div className="h-[39rem] lg:h-auto lg:aspect-[472/400] w-full bg-[#EDECEB] flex-shrink-0 relative flex flex-col items-start justify-end p-[2.5rem] sm:p-[3rem] lg:p-[4rem] overflow-clip">
              {data?.bentoItem4Text && (
                <div className="richtext richtext-start prose-p:text-white/60 prose-strong:text-white prose-strong:font-normal prose-p:text-[2.25rem] lg:prose-p:text-[2.5rem] w-[min(40rem,100%)] relative">
                  <PortableText
                    value={data?.bentoItem4Text}
                    components={richTextComponent}
                  />
                </div>
              )}

              <Image
                className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 !w-calc(100%-4rem)] pointer-events-none"
                src={bento4}
                quality={95}
                alt=""
              />
            </div>

            {/* bento item 5 */}
            <div className="h-[42rem] lg:h-auto lg:aspect-[472/400] w-full bg-[#0A1A1D] flex-shrink-0 relative flex items-center justify-center p-[2.5rem] lg:py-[4rem]">
              <Image
                className="absolute !w-[calc(100%+2px)] !max-w-none !h-full object-cover"
                src={data?.settings?.language === "en" ? bento5EN : bento5}
                quality={95}
                alt=""
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
